import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

const THEME_KEY = 'theme';
const VALID = ['light', 'dark', 'system'];

// Reads the OS colour-scheme preference; safe under jsdom/SSR where matchMedia
// may be absent.
function systemPrefersDark() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

export function ThemeProvider({ children }) {
  // Preference is one of light | dark | system. Legacy values ('light'/'dark')
  // are still valid; a brand-new browser (nothing saved) follows the OS.
  const [theme, setThemeState] = useState(() => {
    const saved = localStorage.getItem(THEME_KEY);
    return VALID.includes(saved) ? saved : 'system';
  });
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  // Track live OS changes so 'system' updates without a reload.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemDark(e.matches);
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);

  const isDark = theme === 'system' ? systemDark : theme === 'dark';

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    root.classList.toggle('dark', isDark);
    body.classList.toggle('dark', isDark);
    localStorage.setItem(THEME_KEY, theme);
  }, [isDark, theme]);

  const setTheme = (pref) => {
    if (VALID.includes(pref)) setThemeState(pref);
  };

  // Binary toggle (used by the header sun/moon control): commit to an explicit
  // light/dark opposite of whatever is currently resolved.
  const toggleTheme = () => setThemeState(isDark ? 'light' : 'dark');

  return (
    <ThemeContext.Provider value={{ theme, setTheme, isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
